import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ArrowForward, ContentCopy } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

const developerFeatures = [
  {
    title: "Cloud-Cloud Linking",
    description:
      "Link your device cloud to the mHome platform, to make your products accessible to more users. We provide super easy adapter to connect your cloud if you already integrated with Amazon Alexa, Google Home or SmartThings.",
    link: "/developer/websocket",
    linkText: "Go to Console",
    available: false,
  },
  {
    title: "Integration Customization",
    description:
      "Define your unique data integrations and capabilities through the mHome console, enabling users to better explore and leverage your product’s features by creating and running automations.",
    link: "/developer/websocket",
    linkText: "Go to Console",
    available: false,
  },
  {
    title: "Assistant API",
    description:
      "Build your own user experience with our agent APIs, to smartly fulfill user tasks and generate automations for your services.",
    link: "/developer/api",
    linkText: "View API Docs",
    available: false,
  },
  {
    title: "Automation Hosting",
    description:
      "Let mHome platform do the heavy lifting of managing the execution and full lifecycle of your generated automations. You can still use automation APIs to control them.",
    link: "/developer/api",
    linkText: "View API Docs",
    available: false,
  },
  {
    title: "Automation Suggestion",
    description:
      "Create automation templates for your most valuable scenarios in the dev console, and let mHome platform suggest them to the users.",
    link: "/developer/local",
    linkText: "Setup Local",
    available: false,
  },
  {
    title: "Local Communication",
    description:
      "Directly communicate to mHome local hub with our local SDKs. The local solution is fast, secure, light weighted, and Matter compatible.",
    link: "/developer/sdk",
    linkText: "Download SDKs",
    available: false,
  },
];

function DeveloperSection() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 2, sm: 3, lg: 4 },
        backgroundColor: theme.palette.background.section,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Chip
            label="Developer Platform"
            sx={{
              mb: 2,
              backgroundColor: theme.palette.chip.secondary.background,
              color: theme.palette.chip.secondary.color,
              border: `1px solid ${theme.palette.chip.secondary.border}`,
              fontWeight: 600,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "3rem", lg: "3.5rem" },
              fontWeight: "bold",
              color: theme.palette.text.primary,
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            Built for{" "}
            <Box
              component="span"
              sx={{
                background: theme.palette.brand.gradientText,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
              }}
            >
              Developers
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.tertiary,
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Developer console and APIs will be available soon. Contact us now
            for your needs at{" "}
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                color: theme.palette.primary.main,
                fontWeight: "bold",
              }}
            >
              yw@mhome.ai
              <Tooltip title="Copy email address">
                <IconButton
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main + "20",
                    },
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText("yw@mhome.ai");
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {developerFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  backgroundColor: theme.palette.background.card,
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${theme.palette.border.light}`,
                  borderRadius: 3,
                  transition: `all ${theme.transitions.duration.standard}ms ease`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    borderColor: theme.palette.background.cardHover,
                    boxShadow: theme.shadows[12],
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 4,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Title */}
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: "bold",
                      mb: 2,
                      fontSize: "1.25rem",
                    }}
                  >
                    {feature.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.tertiary,
                      lineHeight: 1.6,
                      mb: 3,
                      flex: 1,
                    }}
                  >
                    {feature.description}
                  </Typography>

                  {/* Link Button */}
                  <Button
                    variant="outlined"
                    endIcon={<ArrowForward />}
                    href={feature.available ? feature.link : undefined}
                    disabled={!feature.available}
                    sx={{
                      color: feature.available
                        ? theme.palette.primary.main
                        : theme.palette.text.disabled,
                      borderColor: feature.available
                        ? theme.palette.primary.main
                        : theme.palette.border.light,
                      textTransform: "none",
                      fontWeight: 600,
                      alignSelf: "flex-start",
                      "&:hover": feature.available
                        ? {
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.text.white,
                            borderColor: theme.palette.primary.main,
                          }
                        : {},
                      "&.Mui-disabled": {
                        color: theme.palette.text.disabled,
                        borderColor: theme.palette.border.light,
                      },
                    }}
                  >
                    {feature.available ? feature.linkText : "Available Soon"}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default DeveloperSection;
